import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { RecentPoem } from '../types/poetry';

interface ReadingHistoryContextType {
  recentPoems: RecentPoem[];
  addToHistory: (poem: RecentPoem) => void;
  clearHistory: () => void;
}

const ReadingHistoryContext = createContext<ReadingHistoryContextType | undefined>(undefined);

const HISTORY_KEY = 'poezija-reading-history';
const MAX_RECENT_POEMS = 10;

export const ReadingHistoryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [recentPoems, setRecentPoems] = useState<RecentPoem[]>(() => {
    try {
      const saved = localStorage.getItem(HISTORY_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Error loading reading history:', e);
    }
    return [];
  });

  useEffect(() => {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(recentPoems));
  }, [recentPoems]);

  const addToHistory = useCallback((poem: RecentPoem) => {
    setRecentPoems(prev => {
      // Remove if already exists
      const filtered = prev.filter(p => p.poemId !== poem.poemId);
      // Add to beginning with current timestamp
      const updated = [{ ...poem, timestamp: Date.now() }, ...filtered];
      // Keep only MAX_RECENT_POEMS
      return updated.slice(0, MAX_RECENT_POEMS);
    });
  }, []);

  const clearHistory = useCallback(() => {
    setRecentPoems([]);
  }, []);

  return (
    <ReadingHistoryContext.Provider value={{ recentPoems, addToHistory, clearHistory }}>
      {children}
    </ReadingHistoryContext.Provider>
  );
};

export const useReadingHistory = (): ReadingHistoryContextType => {
  const context = useContext(ReadingHistoryContext);
  if (!context) {
    throw new Error('useReadingHistory must be used within a ReadingHistoryProvider');
  }
  return context;
};

